import { promptInput, type SettingsRow } from '@bn-games/ui-shell';
import type {
  DefaultColor,
  Difficulty,
  Language,
  Settings,
  ThemeName,
  Variant,
} from '../settings/types.js';
import { t } from '../i18n/I18n.js';

const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'hard', 'expert'];
const COLORS: readonly DefaultColor[] = ['white', 'black', 'random'];
const VARIANTS: readonly Variant[] = ['russian', 'english'];
const THEME_NAMES: readonly ThemeName[] = ['mahogany', 'persian', 'deco', 'monte'];
const LANGUAGES: readonly Language[] = ['en', 'ru'];

/** Minimal contract the row builder needs — both MenuContext and the
 *  pause-menu adapter satisfy this. */
export interface SettingsRowCtx {
  getSettings(): Settings;
  updateSettings(partial: Partial<Settings>): void;
}

export interface BuildSettingsRowsOptions {
  readonly ctx: SettingsRowCtx;
  /** Pause-menu wraps game-config changes with confirm + restart. */
  readonly onGameConfigChange?: (apply: () => void) => void;
  /** Disable game-config rows entirely (online matches). */
  readonly disableGameConfig?: boolean;
}

export function buildSettingsRows(opts: BuildSettingsRowsOptions): readonly SettingsRow[] {
  const { ctx, onGameConfigChange, disableGameConfig = false } = opts;
  const s = ctx.getSettings();

  const applyGameConfig = (partial: Partial<Settings>): void => {
    const apply = (): void => ctx.updateSettings(partial);
    if (onGameConfigChange) onGameConfigChange(apply);
    else apply();
  };

  return [
    {
      kind: 'text',
      key: 'playerName',
      label: t('settings.rows.playerName'),
      value: s.playerName,
      onClick: () => {
        void promptInput({
          title: t('settings.namePrompt'),
          defaultValue: s.playerName,
        }).then((v) => {
          if (v === null) return;
          const trimmed = v.slice(0, 16);
          if (trimmed) ctx.updateSettings({ playerName: trimmed });
        });
      },
      tab: 'interface',
    },
    {
      kind: 'toggle',
      key: 'sound',
      label: t('settings.rows.sound'),
      value: s.sound,
      onClick: () => ctx.updateSettings({ sound: !s.sound }),
      tab: 'audio',
    },
    {
      kind: 'selector',
      key: 'difficulty',
      label: t('settings.rows.difficulty'),
      value: s.difficulty,
      options: DIFFICULTIES,
      format: (v) => t(`settings.difficulties.${v as Difficulty}`),
      onChange: (next) => applyGameConfig({ difficulty: next as Difficulty }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'variant',
      label: t('settings.rows.variant'),
      value: s.variant,
      options: VARIANTS,
      format: (v) => t(`settings.variants.${v as Variant}`),
      onChange: (next) => applyGameConfig({ variant: next as Variant }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'defaultColor',
      label: t('settings.rows.defaultColor'),
      value: s.defaultColor,
      options: COLORS,
      format: (v) => t(`settings.colors.${v as DefaultColor}`),
      onChange: (next) => applyGameConfig({ defaultColor: next as DefaultColor }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'theme',
      label: t('settings.rows.theme'),
      value: s.theme,
      options: THEME_NAMES,
      format: (v) => t(`settings.themes.${v as ThemeName}`),
      onChange: (next) => ctx.updateSettings({ theme: next as ThemeName }),
      tab: 'interface',
    },
    {
      kind: 'selector',
      key: 'language',
      label: t('settings.rows.language'),
      value: s.language,
      options: LANGUAGES,
      format: (v) => t(`settings.languages.${v as Language}`),
      onChange: (next) => ctx.updateSettings({ language: next as Language }),
      tab: 'interface',
    },
    {
      kind: 'toggle',
      key: 'showValidMoves',
      label: t('settings.rows.showValidMoves'),
      value: s.showValidMoves,
      onClick: () => ctx.updateSettings({ showValidMoves: !s.showValidMoves }),
      tab: 'interface',
    },
    {
      kind: 'toggle',
      key: 'afkAssistEnabled',
      label: t('settings.rows.afkAssistEnabled'),
      value: s.afkAssistEnabled,
      onClick: () => ctx.updateSettings({ afkAssistEnabled: !s.afkAssistEnabled }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'afkRemindersEnabled',
      label: t('settings.rows.afkRemindersEnabled'),
      value: s.afkRemindersEnabled,
      onClick: () => ctx.updateSettings({ afkRemindersEnabled: !s.afkRemindersEnabled }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'turnStartSound',
      label: t('settings.rows.turnStartSound'),
      value: s.turnStartSound,
      onClick: () => ctx.updateSettings({ turnStartSound: !s.turnStartSound }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'showAfkPingButton',
      label: t('settings.rows.showAfkPingButton'),
      value: s.showAfkPingButton,
      onClick: () => ctx.updateSettings({ showAfkPingButton: !s.showAfkPingButton }),
      tab: 'gameplay',
    },
  ];
}
